
import { useState, useEffect } from 'react';
import { Recipe, StructuredIngredient } from '@/types/recipe';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { UnitSystem } from '@/utils/unitConversion/types';

export const useRecipeConversion = (recipe: Recipe | null) => {
  const { preferredUnits, setPreferredUnits } = useUnitPreferences();
  const [displayedStructuredIngredients, setDisplayedStructuredIngredients] = useState<StructuredIngredient[] | null>(null);
  const [displayedInstructions, setDisplayedInstructions] = useState<string[]>([]);
  
  // Update displayed recipe based on unit preference
  useEffect(() => {
    if (!recipe) return;
    
    // Set structured ingredients based on preferred unit system
    if (preferredUnits === 'metric') {
      // Use metric structured ingredients if available
      if (recipe.structured_ingredients_metric && recipe.structured_ingredients_metric.length > 0) {
        setDisplayedStructuredIngredients(recipe.structured_ingredients_metric);
      } else if (recipe.structured_ingredients && recipe.structured_ingredients.length > 0) {
        // Fall back to the legacy structured_ingredients field
        setDisplayedStructuredIngredients(recipe.structured_ingredients);
      } else {
        setDisplayedStructuredIngredients(null);
      }
    } else {
      // Use imperial structured ingredients if available
      if (recipe.structured_ingredients_imperial && recipe.structured_ingredients_imperial.length > 0) {
        setDisplayedStructuredIngredients(recipe.structured_ingredients_imperial);
      } else if (recipe.structured_ingredients && recipe.structured_ingredients.length > 0) {
        // Fall back to the legacy structured_ingredients field
        setDisplayedStructuredIngredients(recipe.structured_ingredients);
      } else {
        setDisplayedStructuredIngredients(null);
      }
    }
    
    // Check if metric version exists AND has content
    const hasMetricVersion = recipe.metric_version && 
      recipe.metric_version.instructions && 
      recipe.metric_version.instructions.length > 0;
    
    // Check if imperial version exists AND has content
    const hasImperialVersion = recipe.imperial_version && 
      recipe.imperial_version.instructions && 
      recipe.imperial_version.instructions.length > 0;
    
    // For instructions, use the versioned instructions
    if (preferredUnits === 'metric' && hasMetricVersion) {
      setDisplayedInstructions(recipe.metric_version!.instructions);
      console.log("Using metric version instructions");
    } else if (preferredUnits === 'imperial' && hasImperialVersion) {
      setDisplayedInstructions(recipe.imperial_version!.instructions);
      console.log("Using imperial version instructions");
    } else {
      // Use original recipe instructions as fallback
      setDisplayedInstructions(recipe.instructions);
      
      if (preferredUnits === 'metric' && !hasMetricVersion) {
        console.log("Missing metric version for instructions, using original recipe");
      } else if (preferredUnits === 'imperial' && !hasImperialVersion) {
        console.log("Missing imperial version for instructions, using original recipe");
      }
    }
  }, [recipe, preferredUnits]);

  const handleUnitToggle = (value: UnitSystem) => {
    setPreferredUnits(value);
  };

  return {
    displayedStructuredIngredients,
    displayedInstructions,
    preferredUnits,
    handleUnitToggle
  };
};
